import React, { useEffect, useRef, useState } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { useHistory, useLocation } from 'react-router-dom'
import { MdKeyboardArrowDown, MdRefresh } from 'react-icons/md'
import styles from './styles.module.scss'
import { createNotification } from '../../../notifications/notifications'
import {
  getYears,
  getTerms,
  getGroups,
  getDisciplines,
  getData,
  setFilters,
  setData,
} from '../../../../actions/journal'

// Выбор значения из списка:
// 1) если ранее выбранное значение есть в новом списке — оставить его;
// 2) иначе, если нужно, — текущее (is_current);
// 3) иначе — первый элемент.
const pickId = (list, key, preferred, useCurrent) => {
  if (!list || !list.length) return null
  if (preferred != null && list.some(item => String(item[key]) === String(preferred))) {
    return list.find(item => String(item[key]) === String(preferred))[key]
  }
  if (useCurrent) {
    const current = list.find(item => item.is_current)
    if (current) return current[key]
  }
  return list[0][key]
}

const Select = ({ className, value, options, onChange, disabled, placeholder }) => {
  const hasValue = options.some(o => String(o.value) === String(value))
  return (
    <div className={`${styles.selectWrap} ${className}`}>
      <select
        className={styles.select}
        value={hasValue ? String(value) : ''}
        onChange={e => onChange(e.target.value)}
        disabled={disabled}
      >
        <option value="" disabled hidden>{placeholder || '—'}</option>
        {options.map(o => (
          <option key={o.value} value={String(o.value)}>{o.label}</option>
        ))}
      </select>
      <MdKeyboardArrowDown size={24} className={styles.selectArrow} />
    </div>
  )
}

const Form = () => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const initialized = useRef(false)
  const [busy, setBusy] = useState(false)

  const { years, terms, groups, disciplines } = useSelector(state => state.journal.filters)
  const loading = useSelector(state => state.journal.loading)

  // Выбранные значения хранятся в searchParams.
  const params = new URLSearchParams(location.search)
  const yearId = params.get('year_id')
  const termId = params.get('term_id')
  const gid = params.get('gid')
  const cid = params.get('cid')

  const writeParams = (next) => {
    const sp = new URLSearchParams()
    Object.entries(next).forEach(([key, val]) => {
      if (val != null && val !== '') sp.set(key, val)
    })
    history.replace({ search: sp.toString() })
  }

  // --- шаги каскада (с очисткой зависимых списков и уведомлениями) ---

  const loadTerms = async (yearValue, preferredTerm, useCurrent) => {
    const list = await dispatch(getTerms(yearValue))
    if (!list || !list.length) {
      dispatch(setFilters({ groups: [], disciplines: [] }))
      dispatch(setData(null))
      createNotification('warning', 'Журнал', 'За выбранный учебный год нет учебных периодов')
      return null
    }
    return pickId(list, 'trmid', preferredTerm, useCurrent)
  }

  const loadGroups = async (termValue, preferredGroup) => {
    const list = await dispatch(getGroups(termValue))
    if (!list || !list.length) {
      dispatch(setFilters({ disciplines: [] }))
      dispatch(setData(null))
      createNotification('warning', 'Журнал', 'По выбранному периоду нет доступных групп')
      return null
    }
    return pickId(list, 'gid', preferredGroup, false)
  }

  const loadDisciplines = async (termValue, groupValue, preferredDiscipline) => {
    const list = await dispatch(getDisciplines(termValue, groupValue))
    if (!list || !list.length) {
      dispatch(setData(null))
      createNotification('warning', 'Журнал', 'По выбранной группе нет дисциплин')
      return null
    }
    return pickId(list, 'cid', preferredDiscipline, false)
  }

  // --- обработчики ---

  // Начальная загрузка: год (текущий) → период (текущий) → группа → дисциплина.
  const init = async () => {
    setBusy(true)
    try {
      const yearList = await dispatch(getYears())
      if (!yearList || !yearList.length) {
        dispatch(setFilters({ terms: [], groups: [], disciplines: [] }))
        dispatch(setData(null))
        createNotification('warning', 'Журнал', 'Список учебных годов пуст')
        writeParams({})
        return
      }
      const year = pickId(yearList, 'xp_key', yearId, true)

      const term = await loadTerms(year, termId, true)
      if (term == null) { writeParams({ year_id: year }); return }

      const group = await loadGroups(term, gid)
      if (group == null) { writeParams({ year_id: year, term_id: term }); return }

      const discipline = await loadDisciplines(term, group, cid)
      writeParams({ year_id: year, term_id: term, gid: group, cid: discipline })
    } finally {
      setBusy(false)
    }
  }

  const onYearChange = async (value) => {
    setBusy(true)
    try {
      const term = await loadTerms(value, termId, false)
      if (term == null) { writeParams({ year_id: value }); return }

      const group = await loadGroups(term, gid)
      if (group == null) { writeParams({ year_id: value, term_id: term }); return }

      const discipline = await loadDisciplines(term, group, cid)
      writeParams({ year_id: value, term_id: term, gid: group, cid: discipline })
    } finally {
      setBusy(false)
    }
  }

  const onTermChange = async (value) => {
    setBusy(true)
    try {
      const group = await loadGroups(value, gid)
      if (group == null) { writeParams({ year_id: yearId, term_id: value }); return }

      const discipline = await loadDisciplines(value, group, cid)
      writeParams({ year_id: yearId, term_id: value, gid: group, cid: discipline })
    } finally {
      setBusy(false)
    }
  }

  const onGroupChange = async (value) => {
    setBusy(true)
    try {
      const discipline = await loadDisciplines(termId, value, cid)
      writeParams({ year_id: yearId, term_id: termId, gid: value, cid: discipline })
    } finally {
      setBusy(false)
    }
  }

  const onDisciplineChange = (value) => {
    writeParams({ year_id: yearId, term_id: termId, gid, cid: value })
  }

  const onUpdate = () => {
    if (!termId || !gid || !cid) {
      createNotification('info', 'Журнал', 'Выберите период, группу и дисциплину')
      return
    }
    dispatch(getData(termId, gid, cid))
  }

  useEffect(() => {
    if (initialized.current) return
    initialized.current = true
    init()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const yearOptions = (years || []).map(y => ({ value: y.xp_key, label: y.name }))
  const termOptions = (terms || []).map(t => ({ value: t.trmid, label: t.title }))
  const groupOptions = (groups || []).map(g => ({ value: g.gid, label: g.name }))
  const disciplineOptions = (disciplines || []).map(d => ({
    value: d.cid,
    label: d.title || d.name || d.alias,
  }))

  const canUpdate = Boolean(termId && gid && cid) && !busy && !loading

  console.log(busy)
  console.log(yearOptions)

  return (
    <div className={styles.filters}>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Учебный год</span>
        <Select
          className={styles.cbYear}
          value={yearId}
          options={yearOptions}
          onChange={onYearChange}
          disabled={busy || !yearOptions.length}
          placeholder="Нет данных"
        />
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Учебный период</span>
        <Select
          className={styles.cbTerm}
          value={termId}
          options={termOptions}
          onChange={onTermChange}
          disabled={busy || !termOptions.length}
          placeholder="Нет периодов"
        />
      </div>
      <div className={styles.field}>
        <span className={styles.fieldLabel}>Группа</span>
        <Select
          className={styles.cbGroup}
          value={gid}
          options={groupOptions}
          onChange={onGroupChange}
          disabled={busy || !groupOptions.length}
          placeholder="Нет групп"
        />
      </div>
      <div className={`${styles.field} ${styles.fieldGrow}`}>
        <span className={styles.fieldLabel}>Дисциплина</span>
        <Select
          className={styles.cbDiscipline}
          value={cid}
          options={disciplineOptions}
          onChange={onDisciplineChange}
          disabled={busy || !disciplineOptions.length}
          placeholder="Нет дисциплин"
        />
      </div>
      <button
        type="button"
        className={styles.updateButton}
        onClick={onUpdate}
        disabled={!canUpdate}
      >
        <MdRefresh size={20} />
        Обновить
      </button>
    </div>
  )
}

export default Form
